import { match, P } from 'ts-pattern'

/**
 * A Kafka destination for live query data
 */
export type LiveQueryKafkaDestination = {
  kafkaConsumer: {
    topicName: string
    consumerGroup: string
  }
}

/**
 * A webhook destination for live query data
 */
export type LiveQueryWebhookDestination = {
  webhook: {
    url: string
  }
}

/**
 * A mongo connector destination for live query data.
 *
 * Note: The `mongo` object is intentionally empty. MongoDB destinations are handled
 * by a separate MongoConnector system (see MongoConnectorCreateForm) with its own
 * API and complex configuration (connection string, database, collections, field mappings).
 * This type exists as a discriminator in the LiveQueryDestination union to enable:
 * - Type guards (isMongoDestination, isLiveQueryDestination)
 * - Destination kind validation in forms
 * - Special handling for mongo (e.g., must be filtered, no firehose support)
 */
export type LiveQueryMongoDestination = {
  mongo: Record<string, never>
}

/**
 * The destination of the sink, i.e. configuration for where the sink sends data
 */
export type LiveQueryDestination =
  | LiveQueryKafkaDestination
  | LiveQueryWebhookDestination
  | LiveQueryMongoDestination

/**
 * The params for specifying a sink's destination at creation time. When creating a webhook destination,
 * the configuration is provided by the user at creation time. When creating a Kafka destination, the
 * configuration is provided by the backend as a reaction to the sink being created.
 */
export type LiveQueryDestinationCreateParams =
  | LiveQueryWebhookDestination
  | 'kafka'

export type LiveQueryDestinationKind = 'kafka' | 'webhook' | 'mongo'

export type LiveQueryFilterStrategy = 'filtered' | 'unfiltered'

export const isLiveQueryFilterStrategy = (
  strategy: unknown,
): strategy is LiveQueryFilterStrategy => {
  return strategy === 'filtered' || strategy === 'unfiltered'
}

/**
 * A type guard for the destination kind
 */
export const isLiveQueryDestinationKind = (
  kind: unknown,
): kind is LiveQueryDestinationKind => {
  return kind === 'kafka' || kind === 'webhook' || kind === 'mongo'
}

/**
 * The auth-specific information for customers using Kafka sinks
 */
export type KafkaAuthConfig = {
  /**
   * A base64-encoded string of the CA certificate for the Kafka cluster
   */
  clusterCaCert: string
  /**
   * The base64-encoded password for the CA certificate
   */
  clusterCaCertPassword: string
  /**
   * The base64-encoded string of the user certificate
   */
  userCaCert: string
  /**
   * The base64-encoded password for the user certificate
   */
  userCaCertPassword: string
}

/**
 * Customer facing data for connecting to _our_ Kafka cluster.
 */
export type LiveQuerySinkKafkaConfig = {
  kafka: {
    /**
     * The name of the topic to send data to
     */
    topicName: string
    /**
     * The name of the consumer group to use
     */
    consumerGroup: string
    /**
     * The endpoint of the Kafka cluster
     */
    endpoint: string
    /**
     * The host of the Kafka cluster
     */
    host: string
    /**
     * The auth-specific information for customers using Kafka sinks
     */
    authConfig: KafkaAuthConfig
  }
}

/**
 * A base64-encoded string representing the value of the secret for the webhook
 */
export type LiveQuerySinkWebhookConfig = { webhook: string }

/**
 * The configuration for a sink, which describes how to send data to a destination.
 * Contains sensitive information, such as passwords and certificates (kafka), or secrets used
 * to verify message integrity (webhooks).
 */
export type LiveQuerySinkConfig =
  | LiveQuerySinkKafkaConfig
  | LiveQuerySinkWebhookConfig

export const isKafkaSinkConfig = (
  config: unknown,
): config is LiveQuerySinkKafkaConfig => {
  return match(config)
    .with(
      {
        kafka: P.shape({
          topicName: P.string,
          consumerGroup: P.string,
          endpoint: P.string,
          host: P.string,
          authConfig: P.shape({
            clusterCaCert: P.string,
            clusterCaCertPassword: P.string,
            userCaCert: P.string,
            userCaCertPassword: P.string,
          }),
        }),
      },
      () => true,
    )
    .otherwise(() => false)
}

export const isWebhookSinkConfig = (
  config: unknown,
): config is LiveQuerySinkWebhookConfig => {
  return match(config)
    .with({ webhook: P.string }, () => true)
    .otherwise(() => false)
}

export const isKafkaDestination = (
  destination: unknown,
): destination is LiveQueryKafkaDestination => {
  return match(destination)
    .with(
      {
        kafkaConsumer: P.shape({
          topicName: P.string,
          consumerGroup: P.string,
          cluster: P.instanceOf(Object).or(P.nullish).optional(),
        }),
      },
      () => true,
    )
    .otherwise(() => false)
}

export const isWebhookDestination = (
  destination: Record<string, unknown> | null,
): destination is LiveQueryWebhookDestination => {
  return match(destination)
    .with({ webhook: P.shape({ url: P.string }) }, () => true)
    .otherwise(() => false)
}

export const isMongoDestination = (
  destination: Record<string, unknown> | null,
): destination is LiveQueryMongoDestination => {
  return match(destination)
    .with({ mongo: P.shape({}) }, () => true)
    .otherwise(() => false)
}

export const isLiveQueryDestination = (
  destination: Record<string, unknown>,
): destination is LiveQueryDestination => {
  return (
    isKafkaDestination(destination) ||
    isWebhookDestination(destination) ||
    isMongoDestination(destination)
  )
}

/**
 * The schema of the source, which describes the format of messages delivered:
 *
 * - `typed`: Typed messages are JSON which encode CRDT metadata into the message structure
 * - `untyped`: Untyped messages resemble plain JSON, with no CRDT encodings
 */
export type LiveQuerySourceSchema = 'typed' | 'untyped'

export type LiveQueryDialect = 'DQL' | 'OQB'
export const isLiveQueryDialect = (
  dialect: unknown,
): dialect is LiveQueryDialect => dialect === 'DQL' || dialect === 'OQB'

/**
 * A sink for live query data
 */
export type LiveQuerySink = {
  /**
   * The ID of the sink document
   */
  id: string
  /**
   * An optional, user-defined description of the source
   */
  description?: string
  /**
   * The ID of the source that the sink belongs to. Internally, this is derive from
   * the `liveQuerySourceRef.name` field on a sink document.
   */
  sourceId: string
  /**
   * The destination of the sink
   */
  destination: LiveQueryDestination
  /**
   * A friendly name for the kind of destination the sink is sending data to.
   */
  destinationKind: LiveQueryDestinationKind
  /**
   * The configuration for the sink, which contains sensitive information. This
   * is populated as a reaction to the sink being created, and so may be `null` if
   * the backend is provisioning the sink.
   */
  config: LiveQuerySinkConfig | null
}

/**
 * The params for creating a live query sink
 */
export type LiveQuerySinkCreateParams = Pick<
  LiveQuerySink,
  'description' | 'sourceId'
> & {
  destination: LiveQueryDestinationCreateParams
}

/**
 * A source for live query data, where multiple sinks can be associated with
 */
export type LiveQuerySource = {
  /**
   * The ID of the source document
   */
  id: string
  /**
   * The app ID (UUID) of the app that the source belongs to
   */
  appId: string
  /**
   * The schema of the source, which describes the format of messages delivered:
   * - `typed`: Typed messages are JSON which encode CRDT metadata into the message structure
   * - `untyped`: Untyped messages resemble plain JSON, with no CRDT encodings
   * Note: we are only allowing 'untyped' to be configured, so we shouldn't really
   * concern ourselves with this field too much.
   */
  schema: LiveQuerySourceSchema
  /**
   * The expression for filtering data. This can be:
   * - `null` to represent all data
   * - A DQL fragment (WHERE clause)
   * - A OQB query
   */
  expression: string | null
  /**
   * The dialect of the expression
   */
  dialect: LiveQueryDialect
  /**
   * The collection to query against. If unspecified, it refers to all collections
   * which means the "firehose" of data.
   *
   * Note: There can only be *one* source document with a null collection per app
   */
  collection?: string
  /**
   * The number of partitions to use for the source, value between 1 and 100
   */
  partitions?: number | null
}

export const isDQLSource = (source: LiveQuerySource): boolean =>
  source.dialect === 'DQL'

export type LiveQuerySourceCreateParams = Pick<
  LiveQuerySource,
  'expression' | 'collection' | 'partitions'
>

export type LiveQueryDataBridgeStatus = 'ready' | 'provisioning' | 'error'

/**
 * A representation of a Data Bridge, derived primarily from an individual sink document in the
 * `__live_query_sinks` collection.
 *
 * Note: The sink's source is loaded directly in this object in a nested structure to
 * enforce the relationship between the source and the sink and how it represents a data bridge.
 */
export type LiveQueryDataBridge = LiveQuerySink & {
  /**
   * The source document of the data bridge
   */
  source: LiveQuerySource
  /**
   * The status of the data bridge, which indicates whether it is ready to be used
   */
  status: LiveQueryDataBridgeStatus
}

export const isLegacyDataBridge = (bridge: LiveQueryDataBridge): boolean =>
  bridge.source.dialect === 'OQB'

/**
 * The params for creating a live query data bridge
 */
export type LiveQueryDataBridgeCreateParams = {
  /**
   * The DQL fragment (WHERE clause) to execute to retrieve data. This is dql-syntax
   */
  expression: string | null
  /**
   * The collection to query against. If unspecified, it refers to all collections
   * which means the "firehose" of data.
   *
   * Note: creation will fail if there is already a data bridge with a null collection for the app
   */
  collection?: string
  /**
   * The name of the data bridge, which maps to the description field of the sink document
   */
  name: string
  /**
   * The params for specifying a sink's destination at creation time
   */
  destination: LiveQueryDestinationCreateParams
  /**
   * The number of partitions to use for the source, value between 1 and 100
   */
  partitions?: number | null
}

/**
 * The params for updating an existing live query data bridge
 */
export type LiveQueryDataBridgeUpdateParams = {
  /**
   * The name of the data bridge, which maps to the description field of the sink document
   */
  name: string
}

/**
 * Operations interface for data bridge callbacks
 */
export interface DataBridgeOperations {
  onUpdate?: (params: LiveQueryDataBridgeUpdateParams) => Promise<void>
  onDelete?: () => Promise<void>
  onRefetch?: () => Promise<void>
}
